import { createSlice } from "@reduxjs/toolkit";
import { v5 as uuidv5 } from "uuid";

import { INITIAL_MSGS, checkTranslation, dotLoader } from "utils";

const NAMESPACE = "123e4567-e89b-12d3-a456-426614174000";

const generateMessageUUID = (msg) => {
  const createdAt = msg.createdAt ? new Date(msg.createdAt).toISOString() : new Date().toISOString();
  const uniqueString = `${msg.speaker}-${createdAt}-${msg.message}`.toLowerCase().replace(/\s+/g, "-");

  return uuidv5(uniqueString, NAMESPACE);
};

const loadMessagesFromLocalStorage = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const property_id = urlParams.get("property_id");

  const savedMessages = localStorage.getItem(`${property_id}-messages`);
  const meMessages = [];
  let isMessages = null;

  try {
    isMessages = JSON.parse(savedMessages);
  } catch (e) {
    isMessages = null;
  }

  if (isMessages && isMessages?.length) {
    isMessages = isMessages?.filter((v) => v.message !== "typing" && !v.message?.includes("Typing") && !v.message?.includes("bounce-dot"));
    meMessages.push(...isMessages);
  }

  return meMessages.length > 0 ? meMessages : null;
};

const saveMessagesToLocalStorage = (messages) => {
  if (!messages || !Array.isArray(messages)) {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const property_id = urlParams.get("property_id");

  messages = messages?.slice(-200);

  messages = messages?.map((msg) => ({
    ...msg,
    id: msg.id || generateMessageUUID(msg),
    speaker: msg.speaker || "bot",
    createdAt: msg.createdAt || new Date().toISOString(),
  }));

  localStorage.setItem(`${property_id}-messages`, JSON.stringify(messages));
};

const initialState = {
  messages: INITIAL_MSGS,
  stopScroll: false,
  isFaq: false,
  showAllOptions: false,
  changeLangAlert: false,
  faqLoader: false,
  showPrivacyDisclaimer: false,
  transStatus: "pending",
  pagination: { page: 1, per_page: 20, loadMore: true, lastResLength: 0, loadedFromStorage: false },
};

export const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    resetMessages: (state) => {
      state = initialState;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload || { page: 1, per_page: 20, loadMore: true, lastResLength: 0 };
    },
    setFaqLoader: (state, action) => {
      state.faqLoader = action.payload;
    },
    setShowAllOptions: (state, action) => {
      state.showAllOptions = action.payload;
    },
    setTransStatus: (state, action) => {
      state.transStatus = action.payload;
    },
    formatMessages: (state, action) => {
      const rawMessages = action.payload.length > 0 && action.payload[0].type === "translated" ? action.payload : checkTranslation(action.payload);

      const newMessages = rawMessages.map((msg) => ({
        ...msg,
        id: msg.id || generateMessageUUID(msg),
        createdAt: msg.createdAt || new Date().toISOString(),
      }));

      state.messages = newMessages;
      saveMessagesToLocalStorage(state.messages);
    },

    updateFeedbackMessages: (state) => {
      state.messages = state.messages.map((msg) =>
        msg.subject === "positive feedback" || msg.subject === "negative feedback" ? { ...msg, subject: "" } : msg
      );

      saveMessagesToLocalStorage(state.messages);
    },

    updateContactMessages: (state) => {
      state.messages = state.messages.map((msg) => (msg.subject === "contact us" || msg.subject === "unsatisfactory answer" ? { ...msg, subject: "" } : msg));

      saveMessagesToLocalStorage(state.messages);
    },

    addNewMessage: (state, action) => {
      // const newMessages = checkTranslation(action.payload);

      const newMessages = checkTranslation(action.payload).map((msg) => ({
        ...msg,
        id: msg.id || generateMessageUUID(msg),
        createdAt: msg.createdAt || new Date().toISOString(),
      }));

      state.messages = [...state.messages, ...newMessages];
      state.pagination.page = 1;

      if (state.pagination.loadedFromStorage) {
        state.pagination.loadedFromStorage = false;
      }

      saveMessagesToLocalStorage(state.messages);
    },
    updateMessage: (state, action) => {
      const { id, feedback } = action.payload;

      const arr = state.messages.map((v) => (v.id === id ? { ...v, feedback } : v));
      state.messages = arr;
      saveMessagesToLocalStorage(state.messages);
    },

    loadInitialMessages: (state, action) => {
      const page = action?.payload?.page || "";
      const savedMessages = loadMessagesFromLocalStorage();
      const contactOnly = page == "contact-me";
      const unitsOnly = page == "available-units";
      const feedbackOnly = page == "feedback";

      let newMessages = [];

      if (contactOnly) {
        const trans = checkTranslation([{ message: "fewMoreDetailGet", speaker: "bot", createdAt: new Date().toISOString() }]);
        newMessages = trans;
      } else if (unitsOnly) {
        const loaderMsg = `<div class="hayley-inline-block">${`Loading Units ${dotLoader}`}</div>`;
        const trans = checkTranslation([{ message: loaderMsg, speaker: "bot", createdAt: new Date().toISOString() }]);
        newMessages = trans;
      } else if (feedbackOnly) {
        newMessages = [];
      } else if (savedMessages?.length > 1) {
        const lastMessage = savedMessages?.[savedMessages.length - 1];

        if (lastMessage && lastMessage.isInitMessage) {
          savedMessages[savedMessages.length - 1].date = new Date().toISOString();
          newMessages = [...savedMessages];
          state.pagination.loadedFromStorage = true;
        } else {
          const trans = checkTranslation(INITIAL_MSGS)?.[0];
          const updatedMessages = [...savedMessages, { ...trans, date: new Date().toISOString() }];
          newMessages = updatedMessages;
        }
      } else {
        newMessages = checkTranslation(INITIAL_MSGS);
        state.showPrivacyDisclaimer = true;
      }

      state.messages = newMessages?.map((msg) => ({
        ...msg,
        id: msg.id || generateMessageUUID(msg),
      }));

      state.pagination.page = 1;
      saveMessagesToLocalStorage(state.messages);
    },

    restartMessagesWithLastOnly: (state) => {
      const savedMessages = loadMessagesFromLocalStorage();

      if (savedMessages?.length) {
        const lastMessage = savedMessages[savedMessages.length - 1];

        if (lastMessage) {
          const newMessages = [lastMessage];
          state.messages = newMessages;
          saveMessagesToLocalStorage(newMessages);
        }
      }
    },

    moveToTop: (state) => {
      state.pagination.loadedFromStorage = true;
    },

    removeAndAddMessages: (state, action) => {
      // const newMessages = checkTranslation(action.payload);
      const newMessages = checkTranslation(action.payload).map((msg) => ({
        ...msg,
        id: msg.id || generateMessageUUID(msg),
        createdAt: msg.createdAt || new Date().toISOString(),
      }));

      state.messages = [...state.messages.slice(0, -1), ...newMessages];
      state.pagination.page = 1;

      if (state.pagination.loadedFromStorage) {
        state.pagination.loadedFromStorage = false;
      }

      saveMessagesToLocalStorage(state.messages);
    },
    removeAndAddNoTransMessages: (state, action) => {
      state.messages = [...state.messages.slice(0, -1), ...action.payload];
      saveMessagesToLocalStorage(state.messages);
    },
    updateScroll: (state, action) => {
      state.stopScroll = action.payload;
    },
    updateFaq: (state, action) => {
      state.isFaq = action.payload;
    },
    updateLangAlert: (state, action) => {
      state.changeLangAlert = action.payload;
    },
    setLoadedFromStorage: (state, action) => {
      state.pagination.loadedFromStorage = action.payload;
    },
    filterEditMessages: (state, action) => {
      const filteredEditMsg = state.messages?.map((m) => {
        const { optionsType, ...rest } = m;

        return rest;
      });

      // const newMessages = checkTranslation(action.payload);
      const newMessages = checkTranslation(action.payload).map((msg) => ({
        ...msg,
        id: msg.id || generateMessageUUID(msg),
        createdAt: msg.createdAt || new Date().toISOString(),
      }));

      state.messages = [...filteredEditMsg, ...newMessages];
      saveMessagesToLocalStorage(state.messages);
    },
    removeLoadingMsgs: (state) => {
      state.messages = state.messages?.filter((v) => v.message !== "typing" && !v.message.includes("Typing") && !v.message.includes("bounce-dot"));
    },
  },
});

export const {
  moveToTop,
  formatMessages,
  addNewMessage,
  removeLoadingMsgs,
  filterEditMessages,
  removeAndAddNoTransMessages,
  updateMessage,
  removeAndAddMessages,
  updateScroll,
  updateFaq,
  setPagination,
  setTransStatus,
  setFaqLoader,
  setShowAllOptions,
  updateLangAlert,
  resetMessages,
  loadInitialMessages,
  restartMessagesWithLastOnly,
  setLoadedFromStorage,
  updateFeedbackMessages,
  updateContactMessages,
} = messagesSlice.actions;
export const selectMessages = (state) => state.messages.messages;
export const stopScroll = (state) => state.messages.stopScroll;
export const selectIsFaq = (state) => state.messages.isFaq;
export const selectTransStatus = (state) => state.messages.transStatus;
export const selectLangAlert = (state) => state.messages.changeLangAlert;
export const selectFaqLoader = (state) => state.messages.faqLoader;
export const getShowAllOptions = (state) => state.messages.showAllOptions;
export const chatPagination = (state) => state.messages.pagination;
export const selectShowPrivacyDisclaimer = (state) => state.messages.showPrivacyDisclaimer;

export default messagesSlice.reducer;
